import React, { useState, useEffect, useRef } from 'react';
import CatsAndDogsComputerPlayer from './CatsAndDogsComputerPlayer.js';

// Inline SVG content for Cat (no wrapping <svg>, so it can be placed inside a nested SVG)
const CatContent = () => (
	<>
		{/* Ears (drawn first so head overlaps the base) */}
		<path d="M 18 44 Q 16 6 44 32 Z" fill="#FF8C00"/>
		<path d="M 82 44 Q 84 6 56 32 Z" fill="#FF8C00"/>
		{/* Inner ears */}
		<path d="M 23 42 Q 22 14 42 32 Z" fill="#ffb347"/>
		<path d="M 77 42 Q 78 14 58 32 Z" fill="#ffb347"/>
		{/* Head – large round circle filling most of the viewBox */}
		<circle cx="50" cy="60" r="38" fill="#FF8C00"/>
		{/* Eyes with white sclera */}
		<circle cx="36" cy="54" r="8" fill="#2d1200"/>
		<circle cx="64" cy="54" r="8" fill="#2d1200"/>
		<circle cx="38" cy="52" r="3" fill="white"/>
		<circle cx="66" cy="52" r="3" fill="white"/>
		{/* Nose */}
		<path d="M 50 65 L 45 71 L 55 71 Z" fill="#FF69B4"/>
		{/* Mouth */}
		<path d="M 43 74 Q 50 80 57 74" stroke="#cc6600" strokeWidth="2" fill="none" strokeLinecap="round"/>
		{/* Whiskers */}
		<line x1="4" y1="63" x2="36" y2="65" stroke="#cc6600" strokeWidth="1.5"/>
		<line x1="4" y1="70" x2="36" y2="70" stroke="#cc6600" strokeWidth="1.5"/>
		<line x1="96" y1="63" x2="64" y2="65" stroke="#cc6600" strokeWidth="1.5"/>
		<line x1="96" y1="70" x2="64" y2="70" stroke="#cc6600" strokeWidth="1.5"/>
	</>
);

// Inline SVG content for Dog
const DogContent = () => (
	<>
		{/* Floppy ears (behind head) */}
		<ellipse cx="14" cy="56" rx="15" ry="24" fill="#a0724a"/>
		<ellipse cx="86" cy="56" rx="15" ry="24" fill="#a0724a"/>
		{/* Head – large round circle */}
		<circle cx="50" cy="50" r="38" fill="#c8855a"/>
		{/* Snout */}
		<ellipse cx="50" cy="68" rx="20" ry="14" fill="#a0724a"/>
		{/* Eyes with white sclera */}
		<circle cx="37" cy="44" r="8" fill="white"/>
		<circle cx="63" cy="44" r="8" fill="white"/>
		<circle cx="38" cy="45" r="5" fill="#1a0800"/>
		<circle cx="64" cy="45" r="5" fill="#1a0800"/>
		<circle cx="40" cy="43" r="2" fill="white"/>
		<circle cx="66" cy="43" r="2" fill="white"/>
		{/* Nose */}
		<ellipse cx="50" cy="63" rx="9" ry="6" fill="#1a0800"/>
		<ellipse cx="47" cy="61" rx="3.5" ry="1.8" fill="#5a2a10" opacity="0.5"/>
		{/* Mouth */}
		<line x1="50" y1="69" x2="50" y2="74" stroke="#1a0800" strokeWidth="2"/>
		<path d="M 50 74 Q 42 79 37 77" stroke="#1a0800" strokeWidth="2" fill="none" strokeLinecap="round"/>
		<path d="M 50 74 Q 58 79 63 77" stroke="#1a0800" strokeWidth="2" fill="none" strokeLinecap="round"/>
	</>
);

// The four central cells of the 8×8 board (rows 3–4, cols 3–4)
const CENTRAL_CELLS = new Set([27, 28, 35, 36]);

// Module-level pure helper – no state dependency
const getAdjacentCells = (index) => {
	const row = Math.floor(index / 8);
	const col = index % 8;
	const adjacent = [];
	if (row > 0) adjacent.push(index - 8);
	if (row < 7) adjacent.push(index + 8);
	if (col > 0) adjacent.push(index - 1);
	if (col < 7) adjacent.push(index + 1);
	return adjacent;
};

/**
 * Returns true when every empty cell is either destroyed (both-side neighbour)
 * or permanently owned by one player – meaning the rest of the game is
 * fully determined and can be auto-filled.
 */
const isAutoFillTriggered = (currentBoard) => {
	for (let i = 0; i < 64; i++) {
		if (currentBoard[i] !== null) continue;
		const adj = getAdjacentCells(i);
		const hasCat = adj.some(j => currentBoard[j] === 'cat');
		const hasDog = adj.some(j => currentBoard[j] === 'dog');
		if (hasCat && hasDog) continue; // destroyed – fine
		if (hasDog) {
			const canCatDestroy = adj.some(
				j => currentBoard[j] === null &&
						 !getAdjacentCells(j).some(k => currentBoard[k] === 'dog')
			);
			if (canCatDestroy) return false; // only temporarily dog-owned
			continue; // permanently dog-owned – fine
		}
		if (hasCat) {
			const canDogDestroy = adj.some(
				j => currentBoard[j] === null &&
						 !getAdjacentCells(j).some(k => currentBoard[k] === 'cat')
			);
			if (canDogDestroy) return false; // only temporarily cat-owned
			continue; // permanently cat-owned – fine
		}
		return false; // neutral cell – game not resolved yet
	}
	return true;
};

/**
 * Builds an ordered fill queue for the auto-fill phase.
 * Returns { queue: [{index, piece}], winner } where winner is the player
 * whose opponent runs out of cells to play first.
 */
const buildAutoFillQueue = (currentBoard, startingPlayer) => {
	const catCells = [];
	const dogCells = [];
	for (let i = 0; i < 64; i++) {
		if (currentBoard[i] !== null) continue;
		const adj = getAdjacentCells(i);
		const hasCat = adj.some(j => currentBoard[j] === 'cat');
		const hasDog = adj.some(j => currentBoard[j] === 'dog');
		if (hasDog && !hasCat) {
			const canCatDestroy = adj.some(
				j => currentBoard[j] === null &&
						 !getAdjacentCells(j).some(k => currentBoard[k] === 'dog')
			);
			if (!canCatDestroy) dogCells.push(i);
		} else if (hasCat && !hasDog) {
			const canDogDestroy = adj.some(
				j => currentBoard[j] === null &&
						 !getAdjacentCells(j).some(k => currentBoard[k] === 'cat')
			);
			if (!canDogDestroy) catCells.push(i);
		}
	}

	const queue = [];
	let catIdx = 0, dogIdx = 0;
	let player = startingPlayer;
	while (catIdx < catCells.length || dogIdx < dogCells.length) {
		if (player === 1) {
			if (catIdx < catCells.length) {
				queue.push({ index: catCells[catIdx++], piece: 'cat' });
			} else {
				return { queue, winner: 2 }; // cats have no cell to play → dogs win
			}
		} else {
			if (dogIdx < dogCells.length) {
				queue.push({ index: dogCells[dogIdx++], piece: 'dog' });
			} else {
				return { queue, winner: 1 }; // dogs have no cell to play → cats win
			}
		}
		player = player === 1 ? 2 : 1;
	}
	// Both ran out; the next player to move has nothing → they lose
	return { queue, winner: player === 1 ? 2 : 1 };
};

// Cats and Dogs Game Component
/**
 * CatsAndDogs: A strategic two-player board game on an 8x8 grid
 * 
 * Game Rules:
 * - Players alternate placing their pieces (one player uses cats, the other uses dogs)
 * - Dogs and cats CANNOT be placed orthogonally adjacent to each other (diagonal is allowed)
 * - On their FIRST move, cats must place in one of the 4 central cells;
 *   dogs must place OUTSIDE the 4 central cells
 * - A player loses if they cannot make a valid move on their turn
 * - The game ends when no valid moves are available for the current player
 */
const CatsAndDogs = () => {
	const [gameStarted, setGameStarted] = useState(false);
	const [playerTypes, setPlayerTypes] = useState({ 1: 'human', 2: 'human' });
	const [board, setBoard] = useState(Array(64).fill(null)); // 8x8 = 64 cells
	const [currentPlayer, setCurrentPlayer] = useState(1); // Player 1 (cats) or Player 2 (dogs)
	const [winner, setWinner] = useState(null);
	const [firstMoveDone, setFirstMoveDone] = useState({ 1: false, 2: false });
	const [autoFillQueue, setAutoFillQueue] = useState([]);
	const [computerPlayer] = useState(new CatsAndDogsComputerPlayer());
	const pendingWinnerRef = useRef(null);

	/**
	 * Check if placing a piece at the given index is a valid move
	 * @param {number} index - The cell index to check
	 * @param {number} player - The player making the move (1 or 2)
	 * @param {Array} currentBoard - The current board state
	 * @returns {boolean} - True if the move is valid
	 */
	const isValidMove = (index, player, currentBoard) => {
		// Cell must be empty
		if (currentBoard[index] !== null) {
			return false;
		}

		// First-move placement restriction
		if (!firstMoveDone[player]) {
			if (player === 1 && !CENTRAL_CELLS.has(index)) return false;  // cats: central only
			if (player === 2 && CENTRAL_CELLS.has(index)) return false;   // dogs: outside central only
		}

		// Check all orthogonally adjacent cells
		const adjacentCells = getAdjacentCells(index);
		const opponentPiece = player === 1 ? 'dog' : 'cat';

		for (const adjIndex of adjacentCells) {
			if (currentBoard[adjIndex] === opponentPiece) {
				return false; // Cannot place next to opponent's piece
			}
		}

		return true;
	};

	/**
	 * Check if the current player has any valid moves available
	 * @param {number} player - The player to check
	 * @param {Array} currentBoard - The current board state
	 * @returns {boolean} - True if at least one valid move exists
	 */
	const hasValidMoves = (player, currentBoard) => {
		for (let i = 0; i < 64; i++) {
			if (isValidMove(i, player, currentBoard)) {
				return true;
			}
		}
		return false;
	};

	/**
	 * Determine the background fill colour for a cell.
	 * Occupied cells keep their piece tint.
	 * Empty cells are classified by their orthogonal neighbours:
	 *   - both cat AND dog neighbours  → light red   ("destroyed": neither player can place)
	 *   - only dog neighbour(s)        → light blue  ("belongs to dogs": only dogs can place)
	 *   - only cat neighbour(s)        → light orange ("belongs to cats": only cats can place)
	 *   - no piece neighbours          → neutral gray
	 */
	const getCellFill = (index, currentBoard) => {
		const cell = currentBoard[index];
		if (cell === 'cat') return '#fff3e0';
		if (cell === 'dog') return '#e3f2fd';

		const adjacent = getAdjacentCells(index);
		const hasCatNeighbor = adjacent.some(i => currentBoard[i] === 'cat');
		const hasDogNeighbor = adjacent.some(i => currentBoard[i] === 'dog');

		if (hasCatNeighbor && hasDogNeighbor) return '#ffcccc'; // destroyed – light red

		if (hasDogNeighbor) {
			// Permanently dog-owned if no empty neighbour is free of dog-neighbours
			// (i.e. a cat can never legally land on any neighbour to destroy this cell)
			const canCatEverDestroy = adjacent.some(
				i => currentBoard[i] === null &&
						 !getAdjacentCells(i).some(j => currentBoard[j] === 'dog')
			);
			return canCatEverDestroy ? '#cce8ff' : '#5a9fd4';    // light vs dark blue
		}

		if (hasCatNeighbor) {
			// Permanently cat-owned if no empty neighbour is free of cat-neighbours
			const canDogEverDestroy = adjacent.some(
				i => currentBoard[i] === null &&
						 !getAdjacentCells(i).some(j => currentBoard[j] === 'cat')
			);
			return canDogEverDestroy ? '#ffe5cc' : '#e07a20';    // light vs dark orange
		}

		return '#f0f0f0';                                       // neutral
	};

	/**
	 * Handle cell click - attempt to place a piece
	 * @param {number} index - The cell index clicked
	 */
	const handleClick = (index) => {
		if (winner) {
			return;
		}

		// Check if move is valid
		if (!isValidMove(index, currentPlayer, board)) {
			return;
		}

		// Place the piece
		const newBoard = [...board];
		newBoard[index] = currentPlayer === 1 ? 'cat' : 'dog';
		setBoard(newBoard);

		// Mark first move as done for this player
		if (!firstMoveDone[currentPlayer]) {
			setFirstMoveDone(prev => ({ ...prev, [currentPlayer]: true }));
		}

		// Switch to next player
		const nextPlayer = currentPlayer === 1 ? 2 : 1;

		// Check if next player has any valid moves
		if (!hasValidMoves(nextPlayer, newBoard)) {
			// Current player wins because opponent has no valid moves
			setWinner(currentPlayer);
		} else if (isAutoFillTriggered(newBoard)) {
			// All remaining empty cells are permanently resolved – auto-fill them
			const { queue, winner: autoWinner } = buildAutoFillQueue(newBoard, nextPlayer);
			if (queue.length === 0) {
				setWinner(autoWinner);
			} else {
				pendingWinnerRef.current = autoWinner;
				setAutoFillQueue(queue);
			}
		} else {
			setCurrentPlayer(nextPlayer);
		}
	};

	/**
	 * Reset the game to initial state
	 */
	const resetGame = () => {
		setBoard(Array(64).fill(null));
		setCurrentPlayer(1);
		setWinner(null);
		setFirstMoveDone({ 1: false, 2: false });
		setAutoFillQueue([]);
		pendingWinnerRef.current = null;
		setGameStarted(false);
	};

	/**
	 * Start the game with selected player types
	 */
	const startGame = () => {
		setGameStarted(true);
	};

	/**
	 * Handle player type selection
	 */
	const handlePlayerTypeChange = (player, type) => {
		setPlayerTypes(prev => ({ ...prev, [player]: type }));
	};

	// Step through the auto-fill queue one cell at a time with a short delay
	useEffect(() => {
		if (autoFillQueue.length === 0) return;
		const [next, ...rest] = autoFillQueue;
		const timeoutId = setTimeout(() => {
			setBoard(prev => {
				const nb = [...prev];
				nb[next.index] = next.piece;
				return nb;
			});
			setAutoFillQueue(rest);
			if (rest.length === 0) {
				setWinner(pendingWinnerRef.current);
			}
		}, 150);
		return () => clearTimeout(timeoutId);
	}, [autoFillQueue]);

	// Handle computer player moves
	useEffect(() => {
		if (!gameStarted || winner || autoFillQueue.length > 0) return;

		const isComputerTurn = playerTypes[currentPlayer] === 'computer';

		if (isComputerTurn) {
			// Trigger computer move after a short delay for better UX
			const timeoutId = setTimeout(() => {
				const move = computerPlayer.makeMove(board, currentPlayer);
				if (move !== null) {
					handleClick(move);
				}
			}, 500);

			return () => clearTimeout(timeoutId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPlayer, gameStarted, winner, board, playerTypes, autoFillQueue]);

	/**
	 * Get the current game status message
	 * @returns {string} - Status message
	 */
	const getStatus = () => {
		if (winner) {
			const winnerName = winner === 1 ? 'Gatos' : 'Cães';
			return `Vencedor: ${winnerName}!`;
		} else {
			const currentPiece = currentPlayer === 1 ? 'Gatos' : 'Cães';
			return `Turno Atual: ${currentPiece}`;
		}
	};

	// Show player configuration screen before game starts
	if (!gameStarted) {
		return (
			<div className="game-container">
				<h1 className="game-title">Gatos e Cães</h1>
				<h2 className="config-subtitle">Configuração dos Jogadores</h2>
				<div className="player-config">
					<div className="player-config-item">
						<h3>Jogador 1 (Gatos)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes[1] === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(1, 'human')}
							>
								Humano
							</button>
							<button
								className={`player-type-button ${playerTypes[1] === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(1, 'computer')}
							>
								Computador
							</button>
						</div>
					</div>
					<div className="player-config-item">
						<h3>Jogador 2 (Cães)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes[2] === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(2, 'human')}
							>
								Humano
							</button>
							<button
								className={`player-type-button ${playerTypes[2] === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(2, 'computer')}
							>
								Computador
							</button>
						</div>
					</div>
				</div>
				<button className="new-game-button" onClick={startGame}>
					Iniciar Jogo
				</button>
			</div>
		);
	}

	return (
		<div className="game-container">
			<h1 className="game-title">Gatos e Cães</h1>
			<div className={`game-status ${winner ? 'winner' : ''}`}>
				{getStatus()}
			</div>
			<svg
				viewBox="0 0 800 800"
				className="cats-and-dogs-svg"
			>
				{board.map((cell, index) => {
					const row = Math.floor(index / 8);
					const col = index % 8;
					const x = col * 100;
					const y = row * 100;
					const isHumanTurn = playerTypes[currentPlayer] === 'human';
					const isValid = !winner && isHumanTurn && isValidMove(index, currentPlayer, board);
					const isDisabled = !!winner || !isHumanTurn;
					return (
						<g key={index} onClick={() => !isDisabled && handleClick(index)}>
							<rect
								x={x + 2}
								y={y + 2}
								width={96}
								height={96}
								rx={8}
								fill={getCellFill(index, board)}
								stroke={isValid ? '#a0aec0' : '#ddd'}
								strokeWidth={isValid ? 2 : 1}
								style={{ cursor: isValid ? 'pointer' : 'default' }}
							/>
							{cell && (
								<svg x={x + 4} y={y + 4} width={92} height={92} viewBox="0 0 100 100">
									{cell === 'cat' ? <CatContent /> : <DogContent />}
								</svg>
							)}
						</g>
					);
				})}
			</svg>
			<button className="new-game-button" onClick={resetGame}>
				Novo Jogo
			</button>
		</div>
	);
};

export default CatsAndDogs;
